// features/products/pages/ProductFormPage.tsx (COMPLETE - ALL 6 STEPS WORKING)
/**
 * Product Form Page - Complete 6-Step Wizard
 * 
 * Steps:
 * 1. Basic Info (name, slug, category, type, brand, tags, supplier)
 * 2. Pricing & Tax
 * 3. Identification (barcodes, SKUs, codes)
 * 4. Specifications (pharmacy, expiry, restrictions, dimensions)
 * 5. Variants, UOMs & Images
 * 6. Review & Submit
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { ErrorState } from '@/components/ui/ErrorState';
import { CategoryQuickCreate, CreatedCategory } from '@/features/categories/CategoryQuickCreate';
import {
    getProduct, createProduct, updateProduct, getCategories,
    getProductTags, uploadProductImage,
    ProductData,
} from '../services/productsApi';
import { CategoryCombobox } from '../components/CategoryCombobox';
import { SupplierCombobox } from '../components/SupplierCombobox';
import { DuplicateAlertBanner } from '../components/DuplicateAlertBanner';
import { QuickBarcodeScannerInput } from '@/components/ui/QuickBarcodeScannerInput';
import {
    ArrowLeft, Plus, Trash2, Save, Barcode, Calendar,
    Pill, Ruler, AlertTriangle, Upload, Image, Layers,
    Info, Box, DollarSign, FlaskConical,
    ShieldAlert, RotateCcw, AlertCircle, Sparkles,
    Pencil, X as XIcon, CloudUpload, Star, GripVertical,
    Eye, ClipboardCheck, CheckCircle, ChevronRight, ChevronLeft,
    Warehouse,
    Calculator,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

// ============================================================
// TYPES
// ============================================================

interface Category {
    id: string; name: string; slug: string; parentId: string | null;
}

interface ProductFormData {
    name: string; slug: string; productType: string; categoryId: string;
    description: string; shortDescription: string; brand: string;
    manufacturer: string; supplierId: string;
    supplierName: string; supplierContact: string; supplierPhone: string; supplierEmail: string;
    tags: string;
    barcode: string; sku: string; mpn: string; upc: string; ean: string;
    imageUrls: string[];
    retailPrice: string; wholesalePrice: string; clearancePrice: string;
    costPrice: string; minimumPrice: string; maximumDiscount: string;
    taxRate: string; taxInclusive: boolean; taxCategory: string;
    trackInventory: boolean; stockManagement: string;
    lowStockThreshold: string; reorderPoint: string; reorderQuantity: string;
    isPharmaceutical: boolean; requiresPrescription: boolean;
    drugClassification: string; activeIngredients: string; storageRequirements: string;
    hasExpiry: boolean; shelfLifeDays: string;
    isAgeRestricted: boolean; minimumAge: string; requiresID: boolean;
    weight: string; weightUnit: string; length: string; width: string; height: string; dimensionUnit: string;
    isReturnable: boolean; returnPeriodDays: string;
    variants: VariantFormData[];
    uoms: UomFormData[];
    barcodes: BarcodeFormData[];
    uploadedImages: File[];
}

interface VariantFormData {
    name: string; sku: string; barcode: string;
    retailPrice: string; wholesalePrice: string; clearancePrice: string;
    costPrice: string; stockQuantity: string;
}

interface UomFormData {
    name: string; abbreviation: string; conversionRate: string;
    isBaseUom: boolean; barcode: string;
    retailPrice: string; wholesalePrice: string; clearancePrice: string;
}

interface BarcodeFormData {
    barcode: string; barcodeType: string; isPrimary: boolean;
}

interface Step {
    id: number; title: string; subtitle: string; icon: React.ElementType;
}

// ============================================================
// CONSTANTS
// ============================================================

const STEPS: Step[] = [
    { id: 1, title: 'Basic Info', subtitle: 'Name, category, supplier & tags', icon: Info },
    { id: 2, title: 'Pricing & Tax', subtitle: 'Prices & tax settings', icon: DollarSign },
    { id: 3, title: 'Identification', subtitle: 'Barcodes, SKUs & codes', icon: Barcode },
    { id: 4, title: 'Specifications', subtitle: 'Pharmacy, expiry & dimensions', icon: FlaskConical },
    { id: 5, title: 'Variants & Images', subtitle: 'Variations, units & images', icon: Layers },
    { id: 6, title: 'Review & Submit', subtitle: 'Confirm and save', icon: ClipboardCheck },
];

const PRODUCT_TYPES = [
    { value: 'physical', label: 'Physical Product' },
    { value: 'digital', label: 'Digital Product' },
    { value: 'service', label: 'Service' },
    { value: 'bundle', label: 'Bundle/Kit' },
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'finished_good', label: 'Finished Good' },
];

const STOCK_MANAGEMENT_TYPES = [
    { value: 'simple', label: 'Simple (Single Quantity)' },
    { value: 'batch', label: 'Batch/Lot Tracking' },
    { value: 'serial', label: 'Serial Number Tracking' },
    { value: 'none', label: 'No Stock Tracking' },
];

const TAX_CATEGORIES = [
    { value: 'standard', label: 'Standard Rate' },
    { value: 'reduced', label: 'Reduced Rate' },
    { value: 'exempt', label: 'Tax Exempt' },
    { value: 'zero', label: 'Zero Rated' },
];

const DRUG_CLASSIFICATIONS = [
    { value: '', label: 'None' },
    { value: 'OTC', label: 'OTC (Over the Counter)' },
    { value: 'Prescription', label: 'Prescription Only' },
    { value: 'Controlled', label: 'Controlled Substance' },
];

const BARCODE_TYPES = [
    { value: 'EAN-13', label: 'EAN-13' },
    { value: 'UPC-A', label: 'UPC-A' },
    { value: 'CODE-128', label: 'CODE-128' },
    { value: 'QR', label: 'QR Code' },
];

const WEIGHT_UNITS = ['kg', 'g', 'lb', 'oz'];
const DIMENSION_UNITS = ['cm', 'in', 'mm', 'm'];

const INITIAL_FORM_DATA: ProductFormData = {
    name: '', slug: '', productType: 'physical', categoryId: '',
    description: '', shortDescription: '', brand: '', manufacturer: '',
    supplierId: '', supplierName: '', supplierContact: '', supplierPhone: '', supplierEmail: '',
    tags: '', barcode: '', sku: '', mpn: '', upc: '', ean: '',
    imageUrls: [], retailPrice: '', wholesalePrice: '', clearancePrice: '',
    costPrice: '', minimumPrice: '', maximumDiscount: '',
    taxRate: '0', taxInclusive: true, taxCategory: 'standard',
    trackInventory: true, stockManagement: 'simple',
    lowStockThreshold: '10', reorderPoint: '20', reorderQuantity: '50',
    isPharmaceutical: false, requiresPrescription: false,
    drugClassification: '', activeIngredients: '', storageRequirements: '',
    hasExpiry: false, shelfLifeDays: '',
    isAgeRestricted: false, minimumAge: '', requiresID: false,
    weight: '', weightUnit: 'kg', length: '', width: '', height: '', dimensionUnit: 'cm',
    isReturnable: true, returnPeriodDays: '7',
    variants: [],
    uoms: [{ name: 'Piece', abbreviation: 'pc', conversionRate: '1', isBaseUom: true, barcode: '', retailPrice: '', wholesalePrice: '', clearancePrice: '' }],
    barcodes: [],
    uploadedImages: [],
};

// ============================================================
// HELPER COMPONENTS
// ============================================================

function SectionCard({ icon: Icon, title, subtitle, children }: {
    icon: React.ElementType; title: string; subtitle: string; children: React.ReactNode;
}) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50/30 to-white flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 ring-1 ring-emerald-200">
                    <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                    <h3 className="font-semibold text-slate-800">{title}</h3>
                    <p className="text-xs text-slate-500">{subtitle}</p>
                </div>
            </div>
            <div className="p-6 space-y-4">{children}</div>
        </div>
    );
}

function InputField({ label, required, ...props }: { label: string; required?: boolean } & React.InputHTMLAttributes<HTMLInputElement>) {
    return (
        <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                {label} {required && <span className="text-rose-500">*</span>}
            </label>
            <input {...props} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all" />
        </div>
    );
}

function SelectField({ label, required, options, ...props }: { label: string; required?: boolean; options: { value: string; label: string }[] } & React.SelectHTMLAttributes<HTMLSelectElement>) {
    return (
        <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                {label} {required && <span className="text-rose-500">*</span>}
            </label>
            <select {...props} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all">
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}

// ============================================================
// DERIVED BOM COST DISPLAY
// Read-only. Shows what BOM will actually use for this UOM,
// derived from the base UOM cost + this UOM's conversion rate.
// ============================================================

function DerivedBomCostDisplay({
    uom,
    isBase,
    baseUom,
    costPrice,
}: {
    uom: UomFormData;
    isBase: boolean;
    baseUom: UomFormData | undefined;
    costPrice: string;
}) {
    const baseCost = parseFloat(costPrice || '0');

    // Cost not set yet → tell the user where to set it
    if (!baseCost || baseCost <= 0) {
        return (
            <div className="text-xs">
                <p className="text-slate-400 italic">Set cost price on Step 2</p>
            </div>
        );
    }

    // This row IS the base UOM → derived cost = base cost itself
    if (isBase) {
        return (
            <div className="text-xs leading-tight">
                <p className="font-mono font-semibold text-slate-700">
                    ₦{baseCost.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                </p>
                <p className="text-[10px] text-slate-400">= base cost</p>
            </div>
        );
    }

    // No base UOM picked yet → can't derive anything
    if (!baseUom) {
        return (
            <div className="text-xs">
                <p className="text-slate-400 italic">Pick a base UOM first</p>
            </div>
        );
    }

    const rate = parseFloat(uom.conversionRate || '0');
    const baseRate = parseFloat(baseUom.conversionRate || '1') || 1;

    if (!rate) {
        return (
            <div className="text-xs">
                <p className="text-slate-400 italic">Enter conversion rate</p>
            </div>
        );
    }

    const ratio = rate / baseRate;
    const perUom = baseCost * ratio;

    return (
        <div className="text-xs leading-tight">
            <p className="font-mono font-semibold text-emerald-700">
                ₦{perUom.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </p>
            <p className="text-[10px] text-slate-400">
                = {baseCost.toLocaleString()} × {ratio.toFixed(4)}
            </p>
        </div>
    );
}

// ============================================================
// CONVERSION RATE HINT
// Small per-row helper that spells out what the entered rate
// means in plain language, and shows the reverse so the user
// catches the classic "1 bag = 50 kg so I'll type 50" mistake.
// ============================================================

function ConversionRateHint({
    uom,
    baseUom,
}: {
    uom: UomFormData;
    baseUom: UomFormData | undefined;
}) {
    // Base row — schema requires rate to be 1, explain why
    if (uom.isBaseUom) {
        return (
            <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                Base unit — always 1
            </p>
        );
    }

    const unitLabel = uom.abbreviation || uom.name || 'unit';

    if (!baseUom) {
        return (
            <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                Pick a base unit first
            </p>
        );
    }

    const baseLabel = baseUom.abbreviation || baseUom.name || 'base';

    // Nothing typed yet — show the shape of the answer
    if (!uom.conversionRate) {
        return (
            <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                Enter how many {baseLabel} = 1 {unitLabel}
            </p>
        );
    }

    const rate = parseFloat(uom.conversionRate);
    if (!rate || rate <= 0 || Number.isNaN(rate)) {
        return (
            <p className="text-[10px] text-rose-500 mt-1 leading-tight">
                Must be a positive number
            </p>
        );
    }

    const reverse = 1 / rate;

    return (
        <div className="text-[10px] mt-1 leading-tight font-mono">
            <p className="text-emerald-600">
                1 {unitLabel} = {rate} {baseLabel}
            </p>
            <p className="text-slate-400">
                1 {baseLabel} = {reverse.toLocaleString(undefined, { maximumFractionDigits: 4 })} {unitLabel}
            </p>
        </div>
    );
}

// ============================================================
// UOM LIVE PREVIEW
// A live "calculator" panel that shows exactly what BOM and POS
// will use for each unit as the user types. Updates on every
// keystroke: conversion rate, retail price, base UOM choice,
// and the Step 2 cost price all feed this.
// ============================================================

function UomLivePreview({
    uoms,
    baseCost,
}: {
    uoms: UomFormData[];
    baseCost: string;
}) {
    const base = uoms.find(u => u.isBaseUom);
    const cost = parseFloat(baseCost || '0');

    // Nothing meaningful to show until there's a base UOM and a cost
    if (!base || cost <= 0) return null;

    // Only rows with a name + conversion + abbreviation are worth showing
    const displayable = uoms.filter(
        u => u.name || u.abbreviation || u.conversionRate,
    );
    if (displayable.length === 0) return null;

    const baseRate = parseFloat(base.conversionRate || '1') || 1;

    // Any row with retail price set → show POS margin column
    const anyRetail = uoms.some(u => parseFloat(u.retailPrice || '0') > 0);

    return (
        <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                    Live price preview
                </span>
                <span className="text-[10px] text-emerald-500 ml-auto">
                    updates as you type
                </span>
            </div>

            {/* Column headers (desktop) */}
            <div className={clsx(
                'hidden md:grid gap-3 px-2 pb-2 text-[10px] font-bold text-emerald-700 uppercase tracking-wider border-b border-emerald-200',
                anyRetail
                    ? 'grid-cols-[1.4fr_1.2fr_1fr_1fr_0.8fr]'
                    : 'grid-cols-[1.4fr_1.4fr_1fr]',
            )}>
                <div>Unit</div>
                <div>Conversion</div>
                <div className="text-right">BOM cost</div>
                {anyRetail && <div className="text-right">Retail (POS)</div>}
                {anyRetail && <div className="text-right">Margin</div>}
            </div>

            {/* Rows */}
            <div className="space-y-1">
                {displayable.map((u, idx) => {
                    const rate = parseFloat(u.conversionRate || '0');
                    const ratio = rate / baseRate; // 1 kg = ratio × base
                    const derived = u.isBaseUom ? cost : cost * ratio;

                    const unitLabel = u.abbreviation || u.name || `unit ${idx + 1}`;
                    const baseLabel = base.abbreviation || base.name || 'base';

                    // "1 kg = 0.0200 Bag"  +  "1 Bag = 50.0000 kg"
                    const forwardLine = u.isBaseUom
                        ? `1 ${unitLabel} = base`
                        : `1 ${unitLabel} = ${rate} ${baseLabel}`;
                    const reverseLine =
                        !u.isBaseUom && rate > 0
                            ? `1 ${baseLabel} = ${(1 / rate).toLocaleString(undefined, {
                                maximumFractionDigits: 4,
                            })} ${unitLabel}`
                            : '';

                    const retail = parseFloat(u.retailPrice || '0');
                    const margin =
                        retail > 0 && derived > 0
                            ? ((retail - derived) / retail) * 100
                            : null;

                    const isInvalid =
                        !u.isBaseUom && (!rate || ratio <= 0);

                    return (
                        <div
                            key={idx}
                            className={clsx(
                                'grid gap-3 px-2 py-2 rounded-lg text-xs items-center transition-colors',
                                'grid-cols-2 md:grid-cols-[1.4fr_1.2fr_1fr_1fr_0.8fr]',
                                u.isBaseUom && 'bg-emerald-100/60',
                                !u.isBaseUom && 'hover:bg-white/60',
                                isInvalid && 'opacity-50',
                            )}
                        >
                            {/* Unit */}
                            <div className="min-w-0">
                                <p className="font-semibold text-slate-800 truncate">
                                    {unitLabel}
                                </p>
                                {u.isBaseUom && (
                                    <p className="text-[10px] text-emerald-600 font-medium">
                                        base unit
                                    </p>
                                )}
                            </div>

                            {/* Conversion */}
                            <div className="min-w-0 hidden md:block">
                                <p className="font-mono text-[10px] text-slate-600 truncate">
                                    {forwardLine}
                                </p>
                                {reverseLine && (
                                    <p className="font-mono text-[10px] text-slate-400 truncate">
                                        {reverseLine}
                                    </p>
                                )}
                            </div>

                            {/* BOM cost */}
                            <div className="text-right">
                                {isInvalid ? (
                                    <span className="text-[10px] text-slate-400 italic">
                                        invalid rate
                                    </span>
                                ) : (
                                    <>
                                        <p className="font-mono font-bold text-emerald-700">
                                            ₦{derived.toLocaleString(undefined, {
                                                maximumFractionDigits: 2,
                                            })}
                                        </p>
                                        {!u.isBaseUom && (
                                            <p className="text-[9px] text-emerald-500 font-mono">
                                                {cost.toLocaleString()} × {ratio.toFixed(4)}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Retail (POS) — desktop only, mobile ignores */}
                            {anyRetail && (
                                <div className="text-right hidden md:block">
                                    {retail > 0 ? (
                                        <p className="font-mono text-slate-700">
                                            ₦{retail.toLocaleString()}
                                        </p>
                                    ) : (
                                        <span className="text-[10px] text-slate-300">—</span>
                                    )}
                                </div>
                            )}

                            {/* Margin — desktop only */}
                            {anyRetail && (
                                <div className="text-right hidden md:block">
                                    {margin !== null ? (
                                        <span
                                            className={clsx(
                                                'font-bold',
                                                margin >= 20
                                                    ? 'text-emerald-600'
                                                    : margin >= 0
                                                        ? 'text-amber-600'
                                                        : 'text-rose-600',
                                            )}
                                        >
                                            {margin.toFixed(1)}%
                                        </span>
                                    ) : (
                                        <span className="text-[10px] text-slate-300">—</span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <p className="text-[10px] text-emerald-600 mt-3 leading-relaxed">
                BOM uses the <strong>BOM cost</strong> column. Retail (POS) is only used if you sell that exact unit at the retail terminal.
                Recipe math: <span className="font-mono">base cost × conversion ratio</span>.
            </p>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ProductFormPage() {
    const { productId } = useParams<{ productId: string }>();
    const isEdit = !!productId;
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState<ProductFormData>(INITIAL_FORM_DATA);
    const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set([1]));
    const [showQuickCreateCategory, setShowQuickCreateCategory] = useState(false);
    const [priceWarnings, setPriceWarnings] = useState<{ message: string; type: 'warning' | 'info' }[]>([]);
    const [slugError, setSlugError] = useState<string | undefined>(undefined);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const [imageInput, setImageInput] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    // Queries
    const { data: categories } = useQuery<Category[]>({
        queryKey: ['categories-flat', user?.tenantId],
        queryFn: () => getCategories(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: product, isLoading: loadingProduct } = useQuery<ProductData>({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!productId && !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: async (data: Record<string, unknown>) => {
            const result = isEdit
                ? await updateProduct(user!.tenantId, productId!, data)
                : await createProduct(user!.tenantId, data);
            if (form.uploadedImages.length > 0 && result?.id) {
                const pid = isEdit ? productId! : result.id;
                for (const file of form.uploadedImages) {
                    try { await uploadProductImage(user!.tenantId, pid, file); } catch { }
                }
            }
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            addToast({ type: 'success', title: isEdit ? 'Product updated' : 'Product created' });
            navigate('/products');
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Save failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    // Populate form when editing
    useEffect(() => {
        if (product && isEdit) {
            setForm({
                name: product.name || '', slug: product.slug || '', productType: product.productType || 'physical',
                categoryId: product.categoryId || '', description: product.description || '', shortDescription: product.shortDescription || '',
                brand: product.brand || '', manufacturer: product.manufacturer || '',
                supplierId: product.supplierId || '', supplierName: product.supplierName || '',
                supplierContact: '', supplierPhone: '', supplierEmail: '',
                tags: (product.tags || []).join(', '), barcode: product.barcode || '', sku: product.sku || '',
                mpn: product.mpn || '', upc: product.upc || '', ean: product.ean || '',
                imageUrls: product.imageUrls || [],
                retailPrice: product.retailPrice || '', wholesalePrice: product.wholesalePrice || '', clearancePrice: product.clearancePrice || '',
                costPrice: product.costPrice || '', minimumPrice: product.minimumPrice || '', maximumDiscount: product.maximumDiscount || '',
                taxRate: product.taxRate || '0', taxInclusive: product.taxInclusive ?? true, taxCategory: product.taxCategory || 'standard',
                trackInventory: product.trackInventory ?? true, stockManagement: product.stockManagement || 'simple',
                lowStockThreshold: String(product.lowStockThreshold || 10), reorderPoint: String(product.reorderPoint || ''), reorderQuantity: String(product.reorderQuantity || ''),
                isPharmaceutical: product.isPharmaceutical || false, requiresPrescription: product.requiresPrescription || false,
                drugClassification: product.drugClassification || '', activeIngredients: product.activeIngredients || '', storageRequirements: product.storageRequirements || '',
                hasExpiry: product.hasExpiry || false, shelfLifeDays: String(product.shelfLifeDays || ''),
                isAgeRestricted: product.isAgeRestricted || false, minimumAge: String(product.minimumAge || ''), requiresID: product.requiresID || false,
                weight: product.weight || '', weightUnit: product.weightUnit || 'kg',
                length: product.length || '', width: product.width || '', height: product.height || '', dimensionUnit: product.dimensionUnit || 'cm',
                isReturnable: product.isReturnable ?? true, returnPeriodDays: String(product.returnPeriodDays || 7),
                variants: (product.variants || []).map(v => ({ name: v.name || '', sku: v.sku || '', barcode: v.barcode || '', retailPrice: v.retailPrice || '', wholesalePrice: v.wholesalePrice || '', clearancePrice: v.clearancePrice || '', costPrice: v.costPrice || '', stockQuantity: v.stockQuantity || '0' })),
                uoms: (product.uoms || []).map(u => ({ name: u.name || '', abbreviation: u.abbreviation || '', conversionRate: u.conversionRate || '', isBaseUom: u.isBaseUom || false, barcode: u.barcode || '', retailPrice: u.retailPrice || '', wholesalePrice: u.wholesalePrice || '', clearancePrice: u.clearancePrice || '' })),
                barcodes: (product.barcodes || []).map(b => ({ barcode: b.barcode || '', barcodeType: b.barcodeType || 'EAN-13', isPrimary: b.isPrimary || false })),
                uploadedImages: [],
            });
            setCompletedSteps(new Set([1, 2, 3, 4, 5, 6]));
        }
    }, [product, isEdit]);

    // ============================================================
    // HELPERS
    // ============================================================

    const updateField = (field: keyof ProductFormData, value: unknown) => setForm(prev => ({ ...prev, [field]: value }));

    const handleNameChange = (value: string) => {
        setForm(prev => ({
            ...prev, name: value,
            ...(isEdit || slugManuallyEdited ? {} : { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') }),
        }));
    };

    const handleTagsChange = async (value: string) => {
        updateField('tags', value);
        const lastTag = value.split(',').pop()?.trim() || '';
        if (lastTag.length >= 2) {
            try {
                const suggestions = await getProductTags(user!.tenantId, lastTag);
                setTagSuggestions(suggestions.filter(t => t.toLowerCase().includes(lastTag.toLowerCase())));
                setShowTagDropdown(suggestions.length > 0);
            } catch { setShowTagDropdown(false); }
        } else { setShowTagDropdown(false); }
    };

    const selectTagSuggestion = (tag: string) => {
        const currentTags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
        currentTags.pop();
        currentTags.push(tag);
        updateField('tags', currentTags.join(', ') + ', ');
        setShowTagDropdown(false);
    };

    const validatePrices = useCallback((): { message: string; type: 'warning' | 'info' }[] => {
        const warnings: { message: string; type: 'warning' | 'info' }[] = [];
        const cost = parseFloat(form.costPrice || '0');
        const retail = parseFloat(form.retailPrice || '0');
        if (cost > 0 && retail > 0) {
            if (cost > retail) {
                const loss = cost - retail;
                const margin = ((retail - cost) / retail) * 100;
                warnings.push({
                    type: 'warning',
                    message: `Cost price (₦${cost.toLocaleString()}) exceeds retail price (₦${retail.toLocaleString()}). Unit loss is ₦${loss.toLocaleString()} (${margin.toFixed(1)}% margin). Permitted for loss leaders or clearance, but sales will run at a loss.`
                });
            } else if (cost === retail) {
                warnings.push({
                    type: 'info',
                    message: `Cost price equals retail price (₦${cost.toLocaleString()}). Product is break-even (0.0% gross margin).`
                });
            }
        }
        return warnings;
    }, [form.costPrice, form.retailPrice]);

    useEffect(() => { setPriceWarnings(validatePrices()); }, [validatePrices]);

    useEffect(() => {
        if (form.slug) {
            if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) setSlugError('Only lowercase letters, numbers, and hyphens');
            else setSlugError(undefined);
        } else setSlugError(undefined);
    }, [form.slug]);

    const goToStep = (step: number) => { if (completedSteps.has(step) || step < currentStep) setCurrentStep(step); };
    const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };
    const getStepValidationError = (step: number): string | null => {
        switch (step) {
            case 1:
                if (!form.name.trim()) return 'Product Name is required';
                if (!form.slug.trim()) return 'Product Slug is required';
                if (slugError) return slugError;
                if (!form.categoryId) return 'Category is required';
                return null;
            case 2:
                if (!form.costPrice.trim()) return 'Cost Price is required';
                if (!form.retailPrice.trim()) return 'Retail Price is required';
                if (isNaN(Number(form.costPrice))) return 'Cost Price must be a valid number';
                if (isNaN(Number(form.retailPrice))) return 'Retail Price must be a valid number';
                return null;
            case 5:
                if (form.uoms.filter(u => u.isBaseUom).length !== 1) return 'Exactly one Base Unit of Measure (UOM) is required';
                return null;
            default:
                return null;
        }
    };

    const canProceed = (step: number): boolean => {
        return getStepValidationError(step) === null;
    };

    const nextStep = () => {
        const error = getStepValidationError(currentStep);
        if (error) {
            addToast({ type: 'warning', title: 'Step Incomplete', message: error });
            return;
        }
        setCompletedSteps(prev => new Set([...prev, currentStep, currentStep + 1]));
        setCurrentStep(currentStep + 1);
    };

    const handleSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (currentStep !== 6) {
            return;
        }
        const step1Err = getStepValidationError(1);
        if (step1Err) { addToast({ type: 'error', title: 'Basic Info Incomplete', message: step1Err }); setCurrentStep(1); return; }
        const step2Err = getStepValidationError(2);
        if (step2Err) { addToast({ type: 'error', title: 'Pricing Incomplete', message: step2Err }); setCurrentStep(2); return; }
        const step5Err = getStepValidationError(5);
        if (step5Err) { addToast({ type: 'error', title: 'Units Incomplete', message: step5Err }); setCurrentStep(5); return; }

        const payload: Record<string, unknown> = {
            name: form.name, slug: form.slug, productType: form.productType, categoryId: form.categoryId,
            description: form.description || undefined, shortDescription: form.shortDescription || undefined,
            brand: form.brand || undefined, manufacturer: form.manufacturer || undefined,
            supplierId: form.supplierId || undefined,
            supplierName: form.supplierName || undefined, supplierContact: form.supplierContact || undefined,
            supplierPhone: form.supplierPhone || undefined, supplierEmail: form.supplierEmail || undefined,
            tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
            barcode: form.barcode || undefined, sku: form.sku || undefined, mpn: form.mpn || undefined,
            upc: form.upc || undefined, ean: form.ean || undefined,
            imageUrls: form.imageUrls,
            retailPrice: form.retailPrice, wholesalePrice: form.wholesalePrice || undefined,
            clearancePrice: form.clearancePrice || undefined, costPrice: form.costPrice,
            minimumPrice: form.minimumPrice || undefined, maximumDiscount: form.maximumDiscount || undefined,
            taxRate: form.taxRate, taxInclusive: form.taxInclusive, taxCategory: form.taxCategory,
            trackInventory: form.trackInventory, stockManagement: form.stockManagement,
            lowStockThreshold: parseInt(form.lowStockThreshold) || 10,
            reorderPoint: form.reorderPoint ? parseInt(form.reorderPoint) : undefined,
            reorderQuantity: form.reorderQuantity ? parseInt(form.reorderQuantity) : undefined,
            isPharmaceutical: form.isPharmaceutical, requiresPrescription: form.requiresPrescription,
            drugClassification: form.drugClassification || undefined,
            activeIngredients: form.activeIngredients || undefined,
            storageRequirements: form.storageRequirements || undefined,
            hasExpiry: form.hasExpiry, shelfLifeDays: form.shelfLifeDays ? parseInt(form.shelfLifeDays) : undefined,
            isAgeRestricted: form.isAgeRestricted, minimumAge: form.minimumAge ? parseInt(form.minimumAge) : undefined,
            requiresID: form.requiresID,
            weight: form.weight || undefined, weightUnit: form.weightUnit,
            length: form.length || undefined, width: form.width || undefined, height: form.height || undefined,
            dimensionUnit: form.dimensionUnit,
            isReturnable: form.isReturnable, returnPeriodDays: parseInt(form.returnPeriodDays) || 7,
            variants: form.variants.filter(v => v.name && v.sku).map(v => ({
                name: v.name, sku: v.sku, barcode: v.barcode || undefined,
                retailPrice: v.retailPrice || undefined, wholesalePrice: v.wholesalePrice || undefined,
                clearancePrice: v.clearancePrice || undefined, costPrice: v.costPrice || undefined,
                stockQuantity: v.stockQuantity || '0',
            })),
            uoms: form.uoms.filter(u => u.name && u.abbreviation && u.conversionRate).map(u => ({
                name: u.name, abbreviation: u.abbreviation, conversionRate: u.conversionRate,
                isBaseUom: u.isBaseUom, barcode: u.barcode || undefined,
                retailPrice: u.retailPrice || undefined, wholesalePrice: u.wholesalePrice || undefined,
                clearancePrice: u.clearancePrice || undefined,
            })),
            barcodes: form.barcodes.filter(b => b.barcode).map(b => ({
                barcode: b.barcode, barcodeType: b.barcodeType, isPrimary: b.isPrimary,
            })),
            storeSpecificPrices: [], customFields: {},
        };
        saveMutation.mutate(payload);
    };

    // Variant helpers
    const addVariant = () => setForm(prev => ({ ...prev, variants: [...prev.variants, { name: '', sku: '', barcode: '', retailPrice: '', wholesalePrice: '', clearancePrice: '', costPrice: '', stockQuantity: '0' }] }));
    const removeVariant = (idx: number) => setForm(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
    const updateVariant = (idx: number, field: keyof VariantFormData, value: string) => {
        setForm(prev => { const updated = [...prev.variants]; updated[idx] = { ...updated[idx], [field]: value }; return { ...prev, variants: updated }; });
    };

    // UOM helpers
    const addUom = () => setForm(prev => ({ ...prev, uoms: [...prev.uoms, { name: '', abbreviation: '', conversionRate: '', isBaseUom: false, barcode: '', retailPrice: '', wholesalePrice: '', clearancePrice: '' }] }));
    const removeUom = (idx: number) => setForm(prev => ({ ...prev, uoms: prev.uoms.filter((_, i) => i !== idx) }));
    const updateUom = (idx: number, field: keyof UomFormData, value: string | boolean) => {
        setForm(prev => { const updated = [...prev.uoms]; updated[idx] = { ...updated[idx], [field]: value }; return { ...prev, uoms: updated }; });
    };
    const setBaseUom = (idx: number) => setForm(prev => ({
        ...prev,
        uoms: prev.uoms.map((u, i) => ({
            ...u,
            isBaseUom: i === idx,
            // Schema requires the base UOM to have conversionRate = '1'.
            // Auto-fill it so the backend validation never catches the user off-guard.
            conversionRate: i === idx ? '1' : u.conversionRate,
        })),
    }));

    // Barcode helpers
    const addBarcode = () => setForm(prev => ({ ...prev, barcodes: [...prev.barcodes, { barcode: '', barcodeType: 'EAN-13', isPrimary: prev.barcodes.length === 0 }] }));
    const removeBarcode = (idx: number) => setForm(prev => ({ ...prev, barcodes: prev.barcodes.filter((_, i) => i !== idx) }));
    const updateBarcode = (idx: number, field: keyof BarcodeFormData, value: string | boolean) => {
        setForm(prev => { const updated = [...prev.barcodes]; updated[idx] = { ...updated[idx], [field]: value }; return { ...prev, barcodes: updated }; });
    };

    // Image helpers
    const addImageUrl = () => { if (imageInput.trim()) { setForm(prev => ({ ...prev, imageUrls: [...prev.imageUrls, imageInput.trim()] })); setImageInput(''); } };
    const removeImageUrl = (idx: number) => setForm(prev => ({ ...prev, imageUrls: prev.imageUrls.filter((_, i) => i !== idx) }));
    const handleFileUpload = (files: FileList) => {
        setForm(prev => ({ ...prev, uploadedImages: [...prev.uploadedImages, ...Array.from(files)] }));
    };
    const removeUploadedImage = (idx: number) => setForm(prev => ({ ...prev, uploadedImages: prev.uploadedImages.filter((_, i) => i !== idx) }));

    const formatNaira = (val: string) => val ? `₦${parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '₦0.00';

    if (loadingProduct) {
        return <div className="min-h-screen flex items-center justify-center"><motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} className="w-16 h-16 rounded-2xl border-4 border-emerald-200 border-t-emerald-600" /></div>;
    }

    if (isEdit && !product && !loadingProduct) {
        return <ErrorState title="Product not found" onRetry={() => navigate('/products')} />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/products')} className="p-2 rounded-xl hover:bg-slate-100" type="button"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg">{isEdit ? <Pencil className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}</div>
                            <div><h1 className="text-xl font-bold text-slate-900">{isEdit ? 'Edit Product' : 'New Product'}</h1><p className="text-sm text-slate-500">Step {currentStep} of {STEPS.length} · {STEPS[currentStep - 1].title}</p></div>
                        </div>
                        <button onClick={() => navigate('/products')} className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
                    </div>
                </div>
            </div>

            {/* Step Progress */}
            <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-6">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center gap-1 overflow-x-auto">
                        {STEPS.map((step, idx) => {
                            const isActive = currentStep === step.id;
                            const isCompleted = completedSteps.has(step.id);
                            const isClickable = isCompleted || step.id < currentStep;
                            return (
                                <div key={step.id} className="flex items-center gap-1 flex-shrink-0">
                                    <button type="button" onClick={() => isClickable && goToStep(step.id)} disabled={!isClickable}
                                        className={clsx('flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all',
                                            isActive && 'bg-emerald-100 text-emerald-700 shadow-sm',
                                            isCompleted && !isActive && 'bg-slate-100 text-slate-600',
                                            !isCompleted && !isActive && 'text-slate-400',
                                            isClickable ? 'cursor-pointer hover:bg-slate-100' : 'cursor-default')}>
                                        <div className={clsx('w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0',
                                            isActive && 'bg-emerald-500 text-white', isCompleted && 'bg-emerald-500 text-white',
                                            !isCompleted && !isActive && 'bg-slate-200 text-slate-500')}>
                                            {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : step.id}
                                        </div>
                                        <span className="hidden md:inline whitespace-nowrap">{step.title}</span>
                                    </button>
                                    {idx < STEPS.length - 1 && <div className={clsx('w-6 h-0.5 rounded flex-shrink-0', isCompleted ? 'bg-emerald-400' : 'bg-slate-200')} />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <div className="max-w-5xl mx-auto px-6 lg:px-8 py-6">
                <form onSubmit={(e) => { e.preventDefault(); if (currentStep === 6) handleSubmit(e); }}>
                    <AnimatePresence mode="wait">
                        {/* ================================================================ */}
                        {/* STEP 1: BASIC INFO */}
                        {/* ================================================================ */}
                        {currentStep === 1 && (
                            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                                <SectionCard icon={Info} title="Basic Information" subtitle="Product name, category, and type">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <InputField label="Product Name" required value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="e.g., Indomie Chicken Flavor" />
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Slug <span className="text-rose-500">*</span></label>
                                            <input type="text" value={form.slug} onChange={e => { updateField('slug', e.target.value); setSlugManuallyEdited(true); }}
                                                className={clsx('w-full px-4 py-3 border-2 rounded-xl text-sm font-mono focus:ring-2', slugError ? 'border-rose-300 bg-rose-50' : 'border-slate-200 focus:border-emerald-400 focus:ring-emerald-500/20')} />
                                            {slugError && <p className="text-xs text-rose-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{slugError}</p>}
                                        </div>
                                    </div>

                                    {/* Live Duplicate & Similarity Alert */}
                                    <DuplicateAlertBanner
                                        name={form.name}
                                        sku={form.sku}
                                        barcode={form.barcode}
                                        excludeId={isEdit ? productId : undefined}
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <SelectField label="Product Type" value={form.productType} onChange={e => updateField('productType', e.target.value)} options={PRODUCT_TYPES} />
                                        <CategoryCombobox
                                            categories={categories || []}
                                            selectedId={form.categoryId}
                                            onSelect={(catId) => updateField('categoryId', catId)}
                                            onCreateNew={() => setShowQuickCreateCategory(true)}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <InputField label="Brand" value={form.brand} onChange={e => updateField('brand', e.target.value)} placeholder="e.g., Indomie" />
                                        <InputField label="Manufacturer" value={form.manufacturer} onChange={e => updateField('manufacturer', e.target.value)} />
                                    </div>
                                    {/* Tags with Autocomplete */}
                                    <div className="relative">
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Tags (comma-separated)</label>
                                        <input type="text" value={form.tags} onChange={e => handleTagsChange(e.target.value)}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" placeholder="e.g., organic, imported, bestseller" />
                                        <AnimatePresence>{showTagDropdown && tagSuggestions.length > 0 && (
                                            <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                                                className="absolute z-20 w-full bg-white border border-slate-200 rounded-xl shadow-xl mt-1 max-h-40 overflow-y-auto">
                                                {tagSuggestions.map(tag => (
                                                    <button key={tag} type="button" onClick={() => selectTagSuggestion(tag)} className="w-full text-left px-4 py-2 text-sm hover:bg-emerald-50 hover:text-emerald-700">{tag}</button>
                                                ))}
                                            </motion.div>
                                        )}</AnimatePresence>
                                    </div>
                                    {/* Supplier / Vendor Searchable Combobox */}
                                    <div className="pt-4 border-t border-slate-100">
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Supplier / Vendor</p>
                                        <SupplierCombobox
                                            supplierId={form.supplierId}
                                            supplierName={form.supplierName}
                                            supplierContact={form.supplierContact}
                                            supplierPhone={form.supplierPhone}
                                            supplierEmail={form.supplierEmail}
                                            onChange={(s) => setForm(prev => ({ ...prev, ...s }))}
                                        />
                                    </div>
                                    <div><label className="block text-xs font-semibold text-slate-700 mb-1.5">Short Description</label><input type="text" value={form.shortDescription} onChange={e => updateField('shortDescription', e.target.value)} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" placeholder="Brief description" /></div>
                                    <div><label className="block text-xs font-semibold text-slate-700 mb-1.5">Full Description</label><textarea value={form.description} onChange={e => updateField('description', e.target.value)} rows={3} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 resize-none" /></div>
                                </SectionCard>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* STEP 2: PRICING & TAX */}
                        {/* ================================================================ */}
                        {currentStep === 2 && (
                            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                                <SectionCard icon={DollarSign} title="Pricing & Tax" subtitle="Set prices and tax configuration">
                                    {priceWarnings.length > 0 && (
                                        <div className="space-y-2">
                                            {priceWarnings.map((w, i) => (
                                                <div key={i} className={clsx(
                                                    'p-3.5 rounded-xl border flex items-start gap-2.5 transition-all',
                                                    w.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'
                                                )}>
                                                    <AlertTriangle className={clsx('w-4 h-4 mt-0.5 flex-shrink-0', w.type === 'warning' ? 'text-amber-600' : 'text-blue-600')} />
                                                    <p className="text-xs leading-relaxed font-medium">{w.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <InputField label="Cost Price" required type="number" step="0.01" value={form.costPrice} onChange={e => updateField('costPrice', e.target.value)} placeholder="0.00" />
                                        <InputField label="Retail Price" required type="number" step="0.01" value={form.retailPrice} onChange={e => updateField('retailPrice', e.target.value)} placeholder="0.00" />
                                        <InputField label="Wholesale Price" type="number" step="0.01" value={form.wholesalePrice} onChange={e => updateField('wholesalePrice', e.target.value)} placeholder="0.00" />
                                        <InputField label="Clearance Price" type="number" step="0.01" value={form.clearancePrice} onChange={e => updateField('clearancePrice', e.target.value)} placeholder="0.00" />
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <InputField label="Tax Rate (%)" type="number" step="0.01" value={form.taxRate} onChange={e => updateField('taxRate', e.target.value)} />
                                        <SelectField label="Tax Category" value={form.taxCategory} onChange={e => updateField('taxCategory', e.target.value)} options={TAX_CATEGORIES} />
                                        <InputField label="Min Price" type="number" step="0.01" value={form.minimumPrice} onChange={e => updateField('minimumPrice', e.target.value)} />
                                        <InputField label="Max Discount %" type="number" step="0.01" value={form.maximumDiscount} onChange={e => updateField('maximumDiscount', e.target.value)} />
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.taxInclusive} onChange={e => updateField('taxInclusive', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm text-slate-700">Tax Inclusive (price includes tax)</span></label>
                                    {parseFloat(form.retailPrice) > 0 && parseFloat(form.costPrice) > 0 && (
                                        <div className={clsx(
                                            'p-4 rounded-xl border',
                                            parseFloat(form.retailPrice) >= parseFloat(form.costPrice)
                                                ? 'bg-emerald-50 border-emerald-200'
                                                : 'bg-rose-50 border-rose-200'
                                        )}>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Info className={clsx('w-4 h-4', parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'text-emerald-600' : 'text-rose-600')} />
                                                    <span className={clsx('text-xs font-bold uppercase tracking-wider', parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'text-emerald-700' : 'text-rose-700')}>
                                                        {parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'Margin & Profit Analysis' : 'Negative Margin / Loss Leader Alert'}
                                                    </span>
                                                </div>
                                                <span className={clsx('text-[10px] font-semibold px-2 py-0.5 rounded-full', parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800')}>
                                                    {parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'Profitable' : 'Loss-Making Unit'}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-3 text-center">
                                                <div className="bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-slate-200/50">
                                                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Net / Unit</p>
                                                    <p className={clsx('text-lg font-bold', parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'text-emerald-600' : 'text-rose-600')}>
                                                        ₦{(parseFloat(form.retailPrice) - parseFloat(form.costPrice)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </p>
                                                </div>
                                                <div className="bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-slate-200/50">
                                                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Gross Margin</p>
                                                    <p className={clsx('text-lg font-bold', parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'text-emerald-600' : 'text-rose-600')}>
                                                        {((parseFloat(form.retailPrice) - parseFloat(form.costPrice)) / parseFloat(form.retailPrice) * 100).toFixed(1)}%
                                                    </p>
                                                </div>
                                                <div className="bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-slate-200/50">
                                                    <p className="text-[10px] text-slate-500 uppercase font-semibold">Markup</p>
                                                    <p className={clsx('text-lg font-bold', parseFloat(form.retailPrice) >= parseFloat(form.costPrice) ? 'text-emerald-600' : 'text-rose-600')}>
                                                        {parseFloat(form.costPrice) > 0 ? ((parseFloat(form.retailPrice) - parseFloat(form.costPrice)) / parseFloat(form.costPrice) * 100).toFixed(1) : 0}%
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </SectionCard>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* STEP 3: IDENTIFICATION */}
                        {/* ================================================================ */}
                        {currentStep === 3 && (
                            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                                <SectionCard icon={Barcode} title="Identification Codes" subtitle="Barcodes, SKUs, and product identifiers">
                                    {/* Real-time barcode & SKU duplicate check */}
                                    <DuplicateAlertBanner
                                        sku={form.sku}
                                        barcode={form.barcode}
                                        excludeId={isEdit ? productId : undefined}
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <QuickBarcodeScannerInput
                                            label="Primary Barcode"
                                            value={form.barcode}
                                            onChange={(val) => updateField('barcode', val)}
                                            onScan={(scanned) => updateField('barcode', scanned)}
                                            placeholder="Scan barcode or enter"
                                            helperText="Plug-and-play USB/Bluetooth scanner ready"
                                        />
                                        <InputField label="SKU" value={form.sku} onChange={e => updateField('sku', e.target.value)} placeholder="Stock Keeping Unit" />
                                        <InputField label="MPN" value={form.mpn} onChange={e => updateField('mpn', e.target.value)} placeholder="Manufacturer Part Number" />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <InputField label="UPC" value={form.upc} onChange={e => updateField('upc', e.target.value)} placeholder="Universal Product Code" />
                                        <InputField label="EAN" value={form.ean} onChange={e => updateField('ean', e.target.value)} placeholder="European Article Number" />
                                    </div>
                                    {/* Additional Barcodes */}
                                    <div className="pt-4 border-t border-slate-100">
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Additional Barcodes</p>
                                            <button type="button" onClick={addBarcode} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100"><Plus className="w-3 h-3" /> Add</button>
                                        </div>
                                        {form.barcodes.length === 0 ? (
                                            <p className="text-xs text-slate-400 text-center py-4">No additional barcodes</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {form.barcodes.map((b, idx) => (
                                                    <div key={idx} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg">
                                                        <div className="col-span-5"><input type="text" placeholder="Barcode" value={b.barcode} onChange={e => updateBarcode(idx, 'barcode', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs font-mono focus:ring-2 focus:ring-emerald-500/20" /></div>
                                                        <div className="col-span-3"><select value={b.barcodeType} onChange={e => updateBarcode(idx, 'barcodeType', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-xs bg-white">{BARCODE_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}</select></div>
                                                        <div className="col-span-2 flex items-center"><label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={b.isPrimary} onChange={e => updateBarcode(idx, 'isPrimary', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /> Primary</label></div>
                                                        <div className="col-span-2 flex items-center justify-end"><button type="button" onClick={() => removeBarcode(idx)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button></div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </SectionCard>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* STEP 4: SPECIFICATIONS */}
                        {/* ================================================================ */}
                        {currentStep === 4 && (
                            <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                                {/* Stock Management Settings */}
                                <SectionCard icon={Warehouse} title="Stock Management" subtitle="How inventory is tracked for this product">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <SelectField label="Stock Management" value={form.stockManagement} onChange={e => updateField('stockManagement', e.target.value)} options={STOCK_MANAGEMENT_TYPES} />
                                        <div className="flex items-end pb-2">
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.trackInventory} onChange={e => updateField('trackInventory', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm font-medium text-slate-700">Track Inventory</span></label>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <InputField label="Low Stock Alert" type="number" value={form.lowStockThreshold} onChange={e => updateField('lowStockThreshold', e.target.value)} />
                                        <InputField label="Reorder Point" type="number" value={form.reorderPoint} onChange={e => updateField('reorderPoint', e.target.value)} />
                                        <InputField label="Reorder Quantity" type="number" value={form.reorderQuantity} onChange={e => updateField('reorderQuantity', e.target.value)} />
                                    </div>
                                </SectionCard>

                                {/* Pharmacy */}
                                <SectionCard icon={Pill} title="Pharmacy Settings" subtitle="Pharmaceutical product configuration">
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.isPharmaceutical} onChange={e => updateField('isPharmaceutical', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm font-medium text-slate-700">This is a pharmaceutical product</span></label>
                                    {form.isPharmaceutical && (
                                        <div className="space-y-4 pl-4 border-l-2 border-emerald-200">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <SelectField label="Drug Classification" value={form.drugClassification} onChange={e => updateField('drugClassification', e.target.value)} options={DRUG_CLASSIFICATIONS} />
                                                <InputField label="Storage Requirements" value={form.storageRequirements} onChange={e => updateField('storageRequirements', e.target.value)} placeholder="e.g., Store below 25°C" />
                                            </div>
                                            <InputField label="Active Ingredients" value={form.activeIngredients} onChange={e => updateField('activeIngredients', e.target.value)} placeholder="e.g., Paracetamol 500mg" />
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.requiresPrescription} onChange={e => updateField('requiresPrescription', e.target.checked)} className="rounded border-slate-300 text-rose-600" /><span className="text-sm font-medium text-rose-600">Requires Prescription</span></label>
                                        </div>
                                    )}
                                </SectionCard>

                                {/* Expiry */}
                                <SectionCard icon={Calendar} title="Expiry & Shelf Life" subtitle="Expiry date tracking">
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.hasExpiry} onChange={e => updateField('hasExpiry', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm font-medium text-slate-700">This product has an expiry date</span></label>
                                    {form.hasExpiry && <InputField label="Shelf Life (Days)" type="number" value={form.shelfLifeDays} onChange={e => updateField('shelfLifeDays', e.target.value)} placeholder="e.g., 365" />}
                                </SectionCard>

                                {/* Restrictions */}
                                <SectionCard icon={ShieldAlert} title="Restrictions" subtitle="Age and ID requirements">
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.isAgeRestricted} onChange={e => updateField('isAgeRestricted', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm font-medium text-slate-700">Age Restricted</span></label>
                                    {form.isAgeRestricted && <InputField label="Minimum Age" type="number" value={form.minimumAge} onChange={e => updateField('minimumAge', e.target.value)} placeholder="e.g., 18" />}
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.requiresID} onChange={e => updateField('requiresID', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm font-medium text-slate-700">Requires ID Verification</span></label>
                                </SectionCard>

                                {/* Dimensions */}
                                <SectionCard icon={Ruler} title="Dimensions & Weight" subtitle="Physical measurements">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <InputField label="Weight" type="number" step="0.001" value={form.weight} onChange={e => updateField('weight', e.target.value)} placeholder="0.000" />
                                        <SelectField label="Unit" value={form.weightUnit} onChange={e => updateField('weightUnit', e.target.value)} options={WEIGHT_UNITS.map(u => ({ value: u, label: u }))} />
                                        <InputField label="Length" type="number" step="0.01" value={form.length} onChange={e => updateField('length', e.target.value)} placeholder="0.00" />
                                        <SelectField label="Unit" value={form.dimensionUnit} onChange={e => updateField('dimensionUnit', e.target.value)} options={DIMENSION_UNITS.map(u => ({ value: u, label: u }))} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <InputField label="Width" type="number" step="0.01" value={form.width} onChange={e => updateField('width', e.target.value)} placeholder="0.00" />
                                        <InputField label="Height" type="number" step="0.01" value={form.height} onChange={e => updateField('height', e.target.value)} placeholder="0.00" />
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.isReturnable} onChange={e => updateField('isReturnable', e.target.checked)} className="rounded border-slate-300 text-emerald-600" /><span className="text-sm text-slate-700">Product is returnable</span></label>
                                    {form.isReturnable && <InputField label="Return Period (Days)" type="number" value={form.returnPeriodDays} onChange={e => updateField('returnPeriodDays', e.target.value)} />}
                                </SectionCard>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* STEP 5: VARIANTS, UOMS & IMAGES */}
                        {/* ================================================================ */}
                        {currentStep === 5 && (
                            <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                                {/* Variants */}
                                <SectionCard icon={Box} title="Variants" subtitle="Sizes, colors, flavors">
                                    <div className="flex justify-end"><button type="button" onClick={addVariant} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100"><Plus className="w-3 h-3" /> Add Variant</button></div>
                                    {form.variants.length === 0 ? (
                                        <p className="text-sm text-slate-400 text-center py-6">No variants. Add if product comes in different sizes, colors, or flavors.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {form.variants.map((v, idx) => (
                                                <div key={idx} className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-slate-50 rounded-lg">
                                                    <input type="text" placeholder="Name *" value={v.name} onChange={e => updateVariant(idx, 'name', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20" />
                                                    <input type="text" placeholder="SKU *" value={v.sku} onChange={e => updateVariant(idx, 'sku', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs font-mono focus:ring-2 focus:ring-emerald-500/20" />
                                                    <input type="number" step="0.01" placeholder="Retail Price" value={v.retailPrice} onChange={e => updateVariant(idx, 'retailPrice', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20" />
                                                    <div className="flex items-end gap-1">
                                                        <input type="number" placeholder="Stock" value={v.stockQuantity} onChange={e => updateVariant(idx, 'stockQuantity', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20" />
                                                        <button type="button" onClick={() => removeVariant(idx)} className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </SectionCard>

                                {/* UOMs */}
                                <SectionCard icon={Ruler} title="Units of Measure" subtitle="At least one base UOM required">
                                    <div className="flex justify-end">
                                        <button type="button" onClick={addUom} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100">
                                            <Plus className="w-3 h-3" /> Add UOM
                                        </button>
                                    </div>

                                    {/* Explainer banner — what the fields mean + how to compute them */}
                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-4">
                                        <div className="flex items-start gap-3">
                                            <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-blue-700 leading-relaxed space-y-1.5">
                                                <p className="font-semibold text-blue-800">How to set up your units</p>
                                                <ul className="space-y-1 pl-4 list-disc marker:text-blue-400">
                                                    <li>
                                                        <strong>Base unit</strong> — the unit you buy and stock in (e.g. <em>Bag</em>, <em>Carton</em>).
                                                        Its conversion rate is always <code className="font-mono bg-blue-100 px-1 rounded">1</code>.
                                                    </li>
                                                    <li>
                                                        <strong>Every other row</strong> — answers the question: <em>how many base units equal 1 of this unit?</em>
                                                    </li>
                                                    <li>
                                                        <strong>BOM cost</strong> is derived automatically: <code className="font-mono bg-blue-100 px-1 rounded">base cost × conversion rate</code>.
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Worked example — makes the "1/50" calculation obvious */}
                                        <div className="pl-8 pt-3 border-t border-blue-200">
                                            <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider mb-2">
                                                Worked example — Bag of Rice (₦25,000, 50 kg per bag)
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                                                <div className="p-2 bg-white/70 rounded-lg border border-blue-200">
                                                    <p className="font-semibold text-slate-700">Bag <span className="text-[9px] text-blue-600 font-normal">(base)</span></p>
                                                    <p className="text-slate-500 mt-0.5">Rate = <code className="font-mono font-bold text-emerald-700">1</code></p>
                                                    <p className="text-[10px] text-slate-400 mt-1">= the reference point</p>
                                                </div>
                                                <div className="p-2 bg-white/70 rounded-lg border border-blue-200">
                                                    <p className="font-semibold text-slate-700">Kilogram</p>
                                                    <p className="text-slate-500 mt-0.5">
                                                        Rate = <code className="font-mono font-bold text-emerald-700">1 ÷ 50 = 0.02</code>
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-1">1 kg = 0.02 Bag</p>
                                                </div>
                                                <div className="p-2 bg-white/70 rounded-lg border border-blue-200">
                                                    <p className="font-semibold text-slate-700">Gram</p>
                                                    <p className="text-slate-500 mt-0.5">
                                                        Rate = <code className="font-mono font-bold text-emerald-700">1 ÷ 50,000 = 0.00002</code>
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-1">1 g = 0.00002 Bag</p>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-blue-600 mt-3 leading-relaxed">
                                                ⚠️ A common mistake is typing <code className="font-mono">50</code> for the kg row because a bag is 50 kg.
                                                That would mean <em>"1 kg = 50 bags"</em>. Look at the <strong>reverse line</strong> under each conversion input
                                                to confirm — it should read <em>"1 Bag = 50 kg"</em>, not <em>"1 Bag = 0.02 kg"</em>.
                                            </p>
                                        </div>

                                        <div className="pl-8 pt-3 border-t border-blue-200">
                                            <p className="text-[11px] text-blue-600 leading-relaxed">
                                                <strong>Retail price (POS)</strong> is only used if you ring up that exact unit at the retail terminal.
                                                It has no effect on recipe costs.
                                            </p>
                                        </div>
                                    </div>

                                    {/* UOM rows */}
                                    <div className="space-y-2">
                                        {/* Column labels (desktop only) */}
                                        <div className="hidden md:grid md:grid-cols-12 gap-2 px-3 pb-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                            <div className="col-span-3">Name</div>
                                            <div className="col-span-2">Abbr</div>
                                            <div className="col-span-2">
                                                Conversion
                                                <br />
                                                <span className="text-[9px] font-normal text-slate-400 normal-case">
                                                    how many base units = 1 of this
                                                </span>
                                            </div>
                                            <div className="col-span-2">Retail price (POS)</div>
                                            <div className="col-span-2">BOM cost (derived)</div>
                                            <div className="col-span-1 text-right">Base</div>
                                        </div>

                                        {form.uoms.map((u, idx) => {
                                            const currentBase = form.uoms.find(x => x.isBaseUom);
                                            return (
                                                <div key={idx} className="grid grid-cols-2 md:grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg items-start">
                                                    <input
                                                        type="text"
                                                        placeholder="Name *"
                                                        value={u.name}
                                                        onChange={e => updateUom(idx, 'name', e.target.value)}
                                                        className="md:col-span-3 w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Abbr *"
                                                        value={u.abbreviation}
                                                        onChange={e => updateUom(idx, 'abbreviation', e.target.value)}
                                                        className="md:col-span-2 w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20"
                                                    />
                                                    <div className="md:col-span-2">
                                                        <input
                                                            type="number"
                                                            step="0.0001"
                                                            placeholder={u.isBaseUom ? '1' : 'e.g. 0.02'}
                                                            value={u.conversionRate}
                                                            onChange={e => updateUom(idx, 'conversionRate', e.target.value)}
                                                            readOnly={u.isBaseUom}
                                                            className={clsx(
                                                                'w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20',
                                                                u.isBaseUom && 'bg-slate-100 text-slate-500 cursor-not-allowed',
                                                            )}
                                                        />
                                                        <ConversionRateHint uom={u} baseUom={currentBase} />
                                                    </div>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        placeholder="Retail (POS)"
                                                        value={u.retailPrice}
                                                        onChange={e => updateUom(idx, 'retailPrice', e.target.value)}
                                                        className="md:col-span-2 w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/20"
                                                    />
                                                    <div className="md:col-span-2">
                                                        <DerivedBomCostDisplay
                                                            uom={u}
                                                            isBase={u.isBaseUom}
                                                            baseUom={currentBase}
                                                            costPrice={form.costPrice}
                                                        />
                                                    </div>
                                                    <div className="md:col-span-1 flex items-center justify-end gap-1 pt-2">
                                                        <label className="flex items-center gap-1 text-xs cursor-pointer" title="Mark as base UOM">
                                                            <input
                                                                type="radio"
                                                                name="baseUom"
                                                                checked={u.isBaseUom}
                                                                onChange={() => setBaseUom(idx)}
                                                                className="text-emerald-600"
                                                            />
                                                            <span className="md:hidden">Base</span>
                                                        </label>
                                                        {!u.isBaseUom && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeUom(idx)}
                                                                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* LIVE PREVIEW — reacts to every keystroke above */}
                                    <UomLivePreview uoms={form.uoms} baseCost={form.costPrice} />
                                </SectionCard>

                                {/* Images */}
                                <SectionCard icon={Image} title="Product Images" subtitle="Add image URLs or upload files">
                                    <div className="flex gap-2">
                                        <input type="text" value={imageInput} onChange={e => setImageInput(e.target.value)} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" placeholder="Enter image URL" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addImageUrl(); } }} />
                                        <button type="button" onClick={addImageUrl} className="px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
                                    </div>
                                    {form.imageUrls.length > 0 && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {form.imageUrls.map((url, i) => (
                                                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group">
                                                    <img src={url} alt="" className="w-full h-full object-cover" onError={(e: any) => { e.target.style.display = 'none'; }} />
                                                    <button type="button" onClick={() => removeImageUrl(i)} className="absolute top-2 right-2 p-1.5 bg-rose-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* File Upload */}
                                    <div className="pt-4 border-t border-slate-100">
                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Upload Image Files</p>
                                        <div onClick={() => fileInputRef.current?.click()}
                                            className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-emerald-400 transition-colors">
                                            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                                                onChange={e => { if (e.target.files?.length) handleFileUpload(e.target.files); e.target.value = ''; }} />
                                            <CloudUpload className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                            <p className="text-sm font-medium text-slate-500">Click to upload images</p>
                                            <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP · Max 10MB each</p>
                                        </div>
                                        {form.uploadedImages.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {form.uploadedImages.map((file, i) => (
                                                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
                                                        <Image className="w-4 h-4 text-emerald-500" />
                                                        <span className="font-medium text-emerald-700 truncate max-w-[150px]">{file.name}</span>
                                                        <button type="button" onClick={() => removeUploadedImage(i)} className="text-rose-500 hover:text-rose-700"><XIcon className="w-3.5 h-3.5" /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </SectionCard>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* STEP 6: REVIEW & SUBMIT */}
                        {/* ================================================================ */}
                        {currentStep === 6 && (
                            <motion.div key="step6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                                <SectionCard icon={ClipboardCheck} title="Review & Submit" subtitle="Confirm all details before saving">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div className="p-4 bg-slate-50 rounded-xl">
                                            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Basic Info</p>
                                            <div className="space-y-1">
                                                <p><span className="text-slate-500">Name:</span> <span className="font-semibold">{form.name || '—'}</span></p>
                                                <p><span className="text-slate-500">Slug:</span> <span className="font-mono text-xs">{form.slug || '—'}</span></p>
                                                <p><span className="text-slate-500">Type:</span> {form.productType}</p>
                                                <p><span className="text-slate-500">Category:</span> {categories?.find(c => c.id === form.categoryId)?.name || '—'}</p>
                                                {form.brand && <p><span className="text-slate-500">Brand:</span> {form.brand}</p>}
                                                {form.supplierName && <p><span className="text-slate-500">Supplier:</span> {form.supplierName}</p>}
                                                {form.tags && <p><span className="text-slate-500">Tags:</span> {form.tags}</p>}
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-xl">
                                            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Pricing & Tax</p>
                                            <div className="space-y-1">
                                                <p><span className="text-slate-500">Cost:</span> <span className="font-semibold">{formatNaira(form.costPrice)}</span></p>
                                                <p><span className="text-slate-500">Retail:</span> <span className="font-semibold text-emerald-600">{formatNaira(form.retailPrice)}</span></p>
                                                {form.wholesalePrice && <p><span className="text-slate-500">Wholesale:</span> {formatNaira(form.wholesalePrice)}</p>}
                                                <p><span className="text-slate-500">Tax:</span> {form.taxRate}% ({form.taxCategory})</p>
                                                <p><span className="text-slate-500">Tax Inclusive:</span> {form.taxInclusive ? 'Yes' : 'No'}</p>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-xl">
                                            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Identification</p>
                                            <div className="space-y-1">
                                                {form.barcode && <p><span className="text-slate-500">Barcode:</span> <span className="font-mono text-xs">{form.barcode}</span></p>}
                                                {form.sku && <p><span className="text-slate-500">SKU:</span> <span className="font-mono text-xs">{form.sku}</span></p>}
                                                <p><span className="text-slate-500">Extra Barcodes:</span> {form.barcodes.filter(b => b.barcode).length}</p>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-xl">
                                            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Variants & UOMs</p>
                                            <p>{form.variants.filter(v => v.name).length} variant(s)</p>
                                            <p>{form.uoms.filter(u => u.name).length} unit(s)</p>
                                            <p>Base UOM: {form.uoms.find(u => u.isBaseUom)?.abbreviation || '—'}</p>
                                            <p>Images: {form.imageUrls.length} URL(s) · {form.uploadedImages.length} file(s)</p>
                                        </div>
                                        <div className="p-4 bg-slate-50 rounded-xl md:col-span-2">
                                            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Specifications</p>
                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                <p>Stock: {form.trackInventory ? form.stockManagement : 'Not tracked'}</p>
                                                <p>Pharma: {form.isPharmaceutical ? 'Yes' : 'No'}</p>
                                                <p>Expiry: {form.hasExpiry ? `${form.shelfLifeDays} days` : 'No'}</p>
                                                <p>Age Restricted: {form.isAgeRestricted ? `Yes (${form.minimumAge}+)` : 'No'}</p>
                                                <p>Returnable: {form.isReturnable ? `${form.returnPeriodDays} days` : 'No'}</p>
                                                {form.weight && <p>Weight: {form.weight} {form.weightUnit}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                                        <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-semibold text-emerald-700">Ready to {isEdit ? 'update' : 'create'} this product</p>
                                            <p className="text-xs text-emerald-600">All required fields are filled. Click submit to save.</p>
                                        </div>
                                    </div>
                                </SectionCard>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Navigation */}
                    <div className="pt-6 space-y-2">
                        <div className="flex items-center justify-between">
                            <button type="button" onClick={prevStep} disabled={currentStep === 1}
                                className="flex items-center gap-2 px-5 py-3 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Previous</button>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => navigate('/products')} className="px-5 py-3 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
                                {currentStep < STEPS.length ? (
                                    <button type="button" onClick={nextStep}
                                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-emerald-700 hover:to-teal-700 transition">
                                        Next <ChevronRight className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <button key="submit-btn" type="button" onClick={() => handleSubmit()} disabled={saveMutation.isPending}
                                        className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50 hover:from-emerald-700 hover:to-teal-700 transition">
                                        {saveMutation.isPending ? <><RotateCcw className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" />{isEdit ? 'Update' : 'Create'} Product</>}
                                    </button>
                                )}
                            </div>
                        </div>
                        {currentStep < STEPS.length && getStepValidationError(currentStep) && (
                            <p className="text-right text-xs text-amber-600 font-medium flex items-center justify-end gap-1.5 pt-1">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                                {getStepValidationError(currentStep)}
                            </p>
                        )}
                    </div>
                </form>
            </div>

            {/* Quick Create Category Modal */}
            <CategoryQuickCreate isOpen={showQuickCreateCategory} onClose={() => setShowQuickCreateCategory(false)}
                onCreated={(newCategory: CreatedCategory) => {
                    queryClient.invalidateQueries({ queryKey: ['categories-flat'] });
                    queryClient.invalidateQueries({ queryKey: ['categories-tree'] });
                    updateField('categoryId', newCategory.id);
                    addToast({ type: 'success', title: 'Category created and selected' });
                }}
                suggestedSlug={form.name ? form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : undefined}
                existingCategories={(categories || []).map(c => ({ id: c.id, name: c.name, slug: c.slug, parentId: c.parentId }))} />
        </div>
    );
}